/**
 * しろぼんAI Widget Entry Point
 *
 * This file is the entry point for the UMD widget bundle.
 * It exports a global `ShirobonAI` object with `mount` and `unmount` functions.
 *
 * Usage:
 * ```html
 * <div id="shirobon-ai-widget"></div>
 * <script src="shirobon-ai.umd.js"></script>
 * <script>
 *   // userId は省略可能（省略時は匿名セッションIDを自動生成）
 *   ShirobonAI.mount('#shirobon-ai-widget', {
 *     apiUrl: 'https://api.shirobon.net/prod/chat',
 *     mode: 'medical_fee'
 *   });
 * </script>
 * ```
 */

import React, { StrictMode } from "react";
import { createRoot, type Root } from "react-dom/client";
import { FloatingWidget } from "./features/chat/FloatingWidget";
// Import widget-specific CSS (no global styles/preflight)
import cssContent from "./widget.css?inline";

// Inject CSS into the document head
const STYLE_ID = "shirobon-ai-widget-styles";

function injectStyles(): void {
	if (typeof document === "undefined") return;
	if (document.getElementById(STYLE_ID)) return;

	const style = document.createElement("style");
	style.id = STYLE_ID;
	style.textContent = cssContent;
	document.head.appendChild(style);
}

function removeStyles(): void {
	if (typeof document === "undefined") return;
	const style = document.getElementById(STYLE_ID);
	if (style) {
		style.remove();
	}
}

// Anonymous session ID management
const ANON_SESSION_KEY = "shirobon-ai-anon-session";

/**
 * Generate a random UUID-like string
 */
function generateUUID(): string {
	// Use crypto.randomUUID if available, otherwise fallback
	if (typeof crypto !== "undefined" && crypto.randomUUID) {
		return crypto.randomUUID();
	}
	// Fallback for older browsers
	return "anon-xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

/**
 * Get or create anonymous session ID
 * Stored in localStorage for persistence across page reloads
 */
function getAnonymousSessionId(): string {
	if (typeof localStorage === "undefined") {
		// Fallback for SSR or no localStorage
		return generateUUID();
	}

	let sessionId = localStorage.getItem(ANON_SESSION_KEY);
	if (!sessionId) {
		sessionId = `anon-${generateUUID()}`;
		localStorage.setItem(ANON_SESSION_KEY, sessionId);
		console.log(`[ShirobonAI] Created anonymous session: ${sessionId}`);
	}
	return sessionId;
}

/**
 * Widget configuration options
 */
export interface WidgetConfig {
	/** Required: API endpoint URL */
	apiUrl: string;
	/** Optional: User ID for session tracking (auto-generated if not provided) */
	userId?: string;
	/** Optional: Chat mode (default: 'medical_fee') */
	mode?: "medical_fee" | "facility_standard";
	/** Optional: Widget position (default: 'bottom-right') */
	position?: "bottom-right" | "bottom-left";
	/** Optional: z-index for the widget (default: 10000) */
	zIndex?: number;
	/** Optional: Whether to open the widget by default (default: false) */
	defaultOpen?: boolean;
}

/**
 * Internal config with resolved userId
 */
interface ResolvedWidgetConfig extends WidgetConfig {
	userId: string;
}

/**
 * Widget instance returned by mount()
 */
export interface WidgetInstance {
	/** Unmount the widget */
	unmount: () => void;
	/** Update widget configuration */
	updateConfig: (config: Partial<WidgetConfig>) => void;
}

// Store mounted widget instances
const mountedWidgets = new Map<string | HTMLElement, { root: Root; config: ResolvedWidgetConfig }>();

/**
 * Resolve selector to HTMLElement
 */
function resolveContainer(selector: string | HTMLElement): HTMLElement | null {
	if (typeof selector === "string") {
		return document.querySelector(selector);
	}
	return selector;
}

/**
 * Get container key for the map
 */
function getContainerKey(selector: string | HTMLElement): string | HTMLElement {
	if (typeof selector === "string") {
		return selector;
	}
	return selector;
}

/**
 * Mount the しろぼんAI widget to a container element
 *
 * @param selector - CSS selector string or HTMLElement
 * @param config - Widget configuration
 * @returns Widget instance with unmount and updateConfig methods
 *
 * @example
 * // Basic usage
 * ShirobonAI.mount('#widget', { apiUrl: 'https://...' });
 *
 * @example
 * // AJAX callback usage
 * $.ajax({
 *   url: '/api/check_login',
 *   success: function(data) {
 *     if (data.loggedIn) {
 *       ShirobonAI.mount('#widget', {
 *         apiUrl: 'https://0nzb6h1eq1.execute-api.ap-northeast-1.amazonaws.com/prod/chat'
 *       });
 *     }
 *   }
 * });
 */
export function mount(selector: string | HTMLElement, config: WidgetConfig): WidgetInstance {
	// Defensive check for config object
	if (config === undefined || config === null) {
		console.error("[ShirobonAI] mount() error: config is undefined");
		console.error("[ShirobonAI] Usage: ShirobonAI.mount('#container', { apiUrl: 'https://...' })");
		console.error("[ShirobonAI] Received arguments:", { selector, config });
		throw new Error(
			"[ShirobonAI] mount() requires 2 arguments: mount(selector, config). " +
			"config must be an object with at least { apiUrl: 'https://...' }"
		);
	}

	// Defensive check for selector
	if (selector === undefined || selector === null || selector === "") {
		console.error("[ShirobonAI] mount() error: selector is invalid");
		console.error("[ShirobonAI] Usage: ShirobonAI.mount('#container', { apiUrl: 'https://...' })");
		throw new Error(
			"[ShirobonAI] mount() requires a valid selector as first argument. " +
			"Example: ShirobonAI.mount('#shirobon-ai-widget', { apiUrl: '...' })"
		);
	}

	const container = resolveContainer(selector);
	const key = getContainerKey(selector);

	if (!container) {
		throw new Error(`[ShirobonAI] Container not found: ${selector}`);
	}

	// Inject styles on first mount
	injectStyles();

	// Check if already mounted
	if (mountedWidgets.has(key)) {
		console.warn(`[ShirobonAI] Widget already mounted to ${selector}. Unmounting first.`);
		unmount(selector);
	}

	// Validate required config
	if (!config.apiUrl) {
		console.error("[ShirobonAI] mount() error: config.apiUrl is required");
		console.error("[ShirobonAI] Received config:", config);
		throw new Error("[ShirobonAI] config.apiUrl is required");
	}

	// Resolve userId: use provided value or generate anonymous session ID
	const resolvedUserId = config.userId || getAnonymousSessionId();

	// Apply defaults
	const fullConfig: ResolvedWidgetConfig = {
		mode: "medical_fee",
		position: "bottom-right",
		zIndex: 10000,
		defaultOpen: false,
		...config,
		userId: resolvedUserId,
	};

	// Create React root and render
	const root = createRoot(container);
	
	const renderWidget = (cfg: ResolvedWidgetConfig) => {
		root.render(
			<StrictMode>
				<FloatingWidget
					apiUrl={cfg.apiUrl}
					userId={cfg.userId}
					mode={cfg.mode}
					position={cfg.position}
					zIndex={cfg.zIndex}
					defaultOpen={cfg.defaultOpen}
				/>
			</StrictMode>
		);
	};

	renderWidget(fullConfig);

	// Store the mounted instance
	mountedWidgets.set(key, { root, config: fullConfig });

	console.log(`[ShirobonAI] Widget mounted to ${selector}`);

	// Return widget instance
	return {
		unmount: () => unmount(selector),
		updateConfig: (newConfig: Partial<WidgetConfig>) => {
			const instance = mountedWidgets.get(key);
			if (instance) {
				const updatedConfig = { ...instance.config, ...newConfig };
				instance.config = updatedConfig;
				renderWidget(updatedConfig);
				console.log(`[ShirobonAI] Config updated for ${selector}`);
			}
		},
	};
}

/**
 * Unmount the widget from a container
 *
 * @param selector - CSS selector string or HTMLElement
 */
export function unmount(selector: string | HTMLElement): void {
	const key = getContainerKey(selector);
	const instance = mountedWidgets.get(key);

	if (instance) {
		instance.root.unmount();
		mountedWidgets.delete(key);
		console.log(`[ShirobonAI] Widget unmounted from ${selector}`);
	} else {
		console.warn(`[ShirobonAI] No widget mounted to ${selector}`);
	}
}

/**
 * Get the version of the widget
 */
export const version = "1.0.0";

// Export to global scope for UMD usage
if (typeof window !== "undefined") {
	(window as any).ShirobonAI = {
		mount,
		unmount,
		version,
	};
}

